import { FormProvider } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faDeleteLeft } from "@fortawesome/free-solid-svg-icons";
import { FormField } from "../../../components/FormField";
import { useInertiaForm } from "../../../hooks/useInertiaForm";
import ImageSelector from "../Homepage/HomeForm/ImageSelector";
import { MediaAttachmentCard } from "./MediaAttachmentCard";
import { useScreenSize } from "../../../hooks/useScreenSize";

interface AttachmentGroupPreviewProps {
  groupRow?: any;
  onDelete?: (id: number, title: string) => void;
}

export default function AttachmentGroupPreview({
  groupRow = {},
  onDelete = () => { },
}: AttachmentGroupPreviewProps) {
  const newGroup = Object.keys(groupRow).length === 0;
  const {
    formMethods,
    onSubmit,
    isSubmitting,
    onCancel,
  } = useInertiaForm({
    key: 'attachment_group',
    serverData: newGroup ? {
      title: '',
      description: '',
      show_on_homepage: false,
      images: []
    } : {
      ...groupRow,
      images: groupRow.record_attachments.map((img: any) => (img.option))
    },
    options: {
      url: newGroup ? '/admin/attachments/groups' : `/admin/attachments/groups/${groupRow.id}`,
      method: newGroup ? 'post' : 'patch',
    },
    cleanBeforeSubmit: (data) => (
      {
        title: data.title,
        description: data.description,
        show_on_homepage: data.show_on_homepage,
        record_attachment_ids: data.images ? data.images.map((img: any) => img.value) : [],
      }
    )
  });

  const {
    formState: { isDirty }
  } = formMethods;

  const { screenLargerThan } = useScreenSize()

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={onSubmit}
        onKeyDown={(e) => {
          if (e.shiftKey && e.key === 'Enter') {
            e.preventDefault();
            onSubmit(e);
          }
        }}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #ddd',
          borderRadius: '8px',
        }}
      >
        {/* Header */}
        <div style={{
          width: '100%',
        }}>
          <div style={{
            width: '100%',
            backgroundColor: '#f5f5f5',
            borderBottom: '2px solid #ddd',
            padding: '4px 8px',
            fontWeight: 'bold',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '3px'
            }}>
              <span>{groupRow.title}</span>
              {newGroup ? (
                "New Group"
              ) : (
                <div
                  className="tooltip tooltip-bottom cursor-pointer"
                  data-tip="Delete"
                  onClick={() => onDelete(groupRow.id, groupRow.title)}
                >
                  <FontAwesomeIcon icon={faDeleteLeft} />
                </div>
              )}
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '4px',
            }}>
              {isDirty && (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
                  className="btn btn-sm"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="btn btn-sm btn-success"
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
                <FontAwesomeIcon icon={faCheck} />
              </button>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div
          style={{
            flex: screenLargerThan('md') ? undefined : '1',
            borderBottom: '1px solid #ddd',
            padding: '4px',
            backgroundColor: 'white',
            overflowY: 'auto',
          }}
        >
          <FormField.Container style={{
            backgroundColor: 'var(--color-base-300)',
            padding: '4px'
          }}>
            <FormField.Field
              name="title"
              label="Title"
              registerProps={{ required: true }}
            />

            <FormField.Field
              name="description"
              label="Description"
              type="textarea"
              rows={6}
              registerProps={{ required: false }}
            />

            <FormField.Checkbox
              name="show_on_homepage"
              label="Show on homepage"
            />

            <ImageSelector
              name="images"
              isMulti={true}
            />
          </FormField.Container>
        </div>

        {/* Bottom section - could show attachments preview */}
        <div style={{
          backgroundColor: 'white',
          padding: '4px',
          overflowY: 'auto',
          flex: 1,
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            gap: '8px',
            color: '#999'
          }}>
            {newGroup ? (
              "Save to manage attachments"
            ) : (
              <div style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'scroll'
              }}>
                <div>
                  {groupRow.record_attachments_count || 0} attachment{groupRow.record_attachments_count === 1 ? '' : 's'}
                </div>
                <div style={{
                  flex: 1,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
                  alignItems: 'start',
                  alignContent: 'start',  // Packs rows to the top
                }}>
                  {groupRow.record_attachments.map((attachment: any) => (
                    <MediaAttachmentCard
                      key={`attachment-card-${attachment.id}`}
                      attachment={attachment}
                      href={`/admin/attachments/${attachment.id}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
}